import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import SimpleTimePickerSingle from './SimpleTimePickerSingle';

import DatePicker from "react-multi-date-picker";
import { DateObject } from "react-multi-date-picker";
import { apiFetch } from '../../utils/apiClient';

import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import setSeconds from 'date-fns/setSeconds';
import setMilliseconds from 'date-fns/setMilliseconds';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Scheduler.css';

const FUTURE_LIMIT_DAYS = 14;

const locales = {
    'en-GB': enGB,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const Scheduler = () => {

    // local development uses env.development, production routes to /api
    const API_URL = process.env.REACT_APP_API_URL || "/api";

    const [personalInfo, setPersonalInfo] = useState({
        initials: '',
        email: '',
    });
    const [bookingDate, setBookingDate] = useState(undefined);
    const [validationErrors, setValidationErrors] = useState({});
    const [triggerValidation, setTriggerValidation] = useState(false);
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [calendarEvents, setCalendarEvents] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    const now = new Date();
    const [currentDate, setCurrentDate] = useState(now);
    const currentView = Views.WORK_WEEK; // Keep track of the current view
    const minTime = setHours(setMinutes(new Date(), 0), 7);
    const maxTime = setHours(setMinutes(new Date(), 0), 19);
    const getLunchBlockerEvents = (view, date) => {
        const lunchEvents = [];
        const startDate = new Date(date);
        let endDate;

        if (view === 'day') {
            endDate = new Date(date);
        } else if (view === 'week' || view === 'work_week') {
            const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
            endDate = new Date(startOfWeekDate);
            endDate.setDate(endDate.getDate() + 4);
            startDate.setTime(startOfWeekDate.getTime());
        }

        let currentDateIterator = new Date(startDate);
        while (currentDateIterator <= endDate) {
            const dayOfWeek = getDay(currentDateIterator); // 0 for Sunday, 6 for Saturday
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                const lunchStart = setHours(setMinutes(setSeconds(setMilliseconds(new Date(currentDateIterator), 0), 0), 0), 12);
                const lunchEnd = setHours(setMinutes(setSeconds(setMilliseconds(new Date(currentDateIterator), 0), 0), 0), 13);

                lunchEvents.push({
                    start: lunchStart,
                    end: lunchEnd,
                    title: 'Lunch',
                    resource: 'lunch',
                });
            }
            currentDateIterator.setDate(currentDateIterator.getDate() + 1);
        }
        return lunchEvents;
    };

    useEffect(() => {
        setBackgroundEvents(getLunchBlockerEvents(currentView, currentDate));
    }, [currentView, currentDate]);

    const [backgroundEvents, setBackgroundEvents] = useState(getLunchBlockerEvents(currentView, currentDate));

    const [loadingMeetings, setLoadingMeetings] = useState(true); // Add a loading state
    const [meetingsError, setMeetingsError] = useState(null); // Add an error state
    useEffect(() => {
        const fetchMeetings = async () => {
            setLoadingMeetings(true);
            setMeetingsError(null); // Clear any previous errors
            try {
                const bookingsURL = `${API_URL}/meetings`;
                const response = await apiFetch(bookingsURL);

                // unpack the response as it it will be a json object
                // even if backend threw an error
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`${data.error}`);
                }

                // else if the response is ok, set the meetings
                setMeetings(data);

            } catch (error) {
                setMeetingsError(error.message);
            } finally {
                setLoadingMeetings(false);
            }
        };

        fetchMeetings();

        // eslint-disable-next-line
    }, []); // Empty dependency array means this runs only once after the initial render

    useEffect(() => {
        // Map the fetched meetings to the format react-big-calendar expects
        const formattedEvents = meetings.map(meeting => ({
            start: new Date(meeting.meeting_start),
            end: new Date(meeting.meeting_end),
            title: meeting.user_initial || 'Booked', // Use user_initial or a default
            resource: meeting.meeting_id, // Optionally include the meeting ID as a resource
        }));
        setCalendarEvents(formattedEvents);
    }, [meetings]); // Re-run this effect whenever the 'meetings' data changes


    // handles the first name changes
    const handleInputChange = (e) => {

        const { name, value } = e.target;

        let truncatedValue = value;
        if (name === 'initials') {
            truncatedValue = value.toUpperCase();
        }
        if (name === 'initials' && value.length > 2) {
            truncatedValue = value.substring(0, 2).toUpperCase();
        }

        setPersonalInfo({
            ...personalInfo,
            [name]: truncatedValue,
        });

        if (isButtonClicked) {
            // continuously validate form on input change
            setTriggerValidation(true);
        }

    };

    // handles the daily date selection
    const handleDailySelect = (date) => {

        // it is important that the dates are formatted as "YYYY-MM-DD"
        // this ensures that the dates are captured correctly as the user selects them
        // amd they are not converted to UTC. We need to capture the date the user sees/selects

        setBookingDate(date);

        if (isButtonClicked) {
            // continuously validate form on input change
            setTriggerValidation(true);
        }

    };


    const [selectedTime, setSelectedTime] = useState(); // or null

    // handles the time selection
    const handleTimeSelect = (time) => {

        setSelectedTime(time);

        if (isButtonClicked) {
            // continuously validate form on input change
            setTriggerValidation(true);
        }

    };

    const validateForm = useCallback(() => {

        if (!isButtonClicked) {
            // capture first time button has been clicked
            setIsButtonClicked(true);
        }

        const errors = {};
        if (!personalInfo.initials) {
            errors.initials = true;
        }

        if (!personalInfo.email) {
            errors.email = true;
        } else if (!/\S+@\S+\.\S+/.test(personalInfo.email)) {
            errors.email = true;
        }

        if (!bookingDate) {
            errors.bookingDate = true;
        }

        if (!selectedTime || selectedTime === "") {
            // Check if it's empty or the initial "Select Time" value
            // "Select Time" is the placeholder value and has a value of ""
            errors.selectedTime = true;
        }

        setValidationErrors(errors);

        // determine if form is valid
        const isValid = Object.keys(errors).length === 0

        // Reset trigger after validation so it will be called again if set true
        if (triggerValidation)
            setTriggerValidation(false);

        return isValid;

    }, [isButtonClicked, personalInfo, bookingDate, selectedTime, triggerValidation]);


    useEffect(() => {
        if (triggerValidation) {
            validateForm();
        }
    }, [triggerValidation, validateForm]);


    const handleBookRoom = async () => {

        if (!validateForm()) {
            // do not proceed if form is not valid
            return;
        }

        try {


            // convert the selected time to 24 hour format
            const [timePart, ampm] = selectedTime.split(' ');
            const [hourStr, minuteStr] = timePart.split(':');

            let hour = parseInt(hourStr, 10);
            if (ampm === 'PM' && hour !== 12) {
                hour += 12;
            } else if (ampm === 'AM' && hour === 12) {
                hour = 0;
            }

            const newTime24Hour = `${String(hour).padStart(2, '0')}:${minuteStr || '00'}`;


            const payload = {
                meeting_date: bookingDate.format('YYYY-MM-DD'),
                meeting_time: newTime24Hour,
                initials: personalInfo.initials.trim(),
                email: personalInfo.email.trim().toLowerCase(),
                office_id: 1
            };


            const bookingsURL = `${API_URL}/meeting`;

            const response = await apiFetch(bookingsURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`${data.error}`);
            }

            // save the meetings data
            setMeetings(data);

            // open model to show booking has been updated
            setIsModalOpen(true);

            // reset the form
            setPersonalInfo({
                initials: '',
                email: '',
            });
            setBookingDate(undefined);
            setSelectedTime(undefined);
            setValidationErrors({});
            setTriggerValidation(false);
            setIsButtonClicked(false);

        } catch (error) {
            // catch any errors, i.e. ones not caught by the fetch call
            // and also the one thrown by the fetch call
            setApiErrorMessage(error.message);
            setIsErrorModalOpen(true);
        }
    };


    const handleNavigate = (newDate) => {
        setCurrentDate(newDate);
    };

    const CustomEvent = ({ event }) => (
        <div>
            <strong>{event.title}</strong>
            {/* You can choose to not display any time information here */}
        </div>
    )

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div className='scheduler-container'>
            {loadingMeetings ? (
                <p>Loading meetings...</p>
            ) : meetingsError ? (
                <p className='error-text'>
                    {meetingsError}
                </p>
            ) : (
                <div>
                    <Calendar
                        localizer={localizer}
                        events={calendarEvents}
                        backgroundEvents={backgroundEvents}
                        startAccessor="start"
                        endAccessor="end"
                        defaultView={Views.WORK_WEEK}
                        views={[Views.WORK_WEEK]}
                        min={minTime}
                        max={maxTime}
                        components={{
                            event: CustomEvent,
                        }}
                        onNavigate={handleNavigate}
                    />
                    <h2>Booking details</h2>
                    <div className='booking-form-container'>
                        <div className='input-container'>
                            <input
                                type="text"
                                id="initials"
                                name="initials"
                                placeholder="Initials (e.g. AB)"
                                value={personalInfo.initials}
                                onChange={handleInputChange}
                                aria-label="Enter your name"
                            />
                            {validationErrors.initials && (
                                <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                            )}
                        </div>

                        <div className='input-container'>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Email"
                                value={personalInfo.email}
                                onChange={handleInputChange}
                                aria-label="Enter your email"
                            />
                            {validationErrors.email && (
                                <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                            )}
                        </div>

                        <div className='input-container'>
                            <DatePicker
                                containerClassName='custom-scheduler-input'
                                onChange={handleDailySelect}
                                //render={<InputIcon />}
                                maxDate={new DateObject().add(
                                    FUTURE_LIMIT_DAYS,
                                    'days'
                                )}
                                placeholder="Date"
                                numberOfMonths={1}
                                format="dddd DD MMM YYYY"
                                monthYearSeparator="|"
                                weekStartDayIndex={1}
                                value={bookingDate}
                                mapDays={({ date }) => {
                                    const today = new DateObject();
                                    const isWeekend = [0, 6].includes(
                                        date.weekDay.index
                                    );
                                    const isPast = date.toDays() - today.toDays() < 0;
                                    const isFuture =
                                        date.toDays() - today.toDays() >
                                        FUTURE_LIMIT_DAYS;

                                    if (isPast || isFuture || isWeekend) {
                                        return {
                                            disabled: true,
                                            style: {
                                                color: '#ccc',
                                                textDecoration: isPast
                                                    ? 'line-through'
                                                    : 'none',
                                            },
                                        };
                                    }
                                }}
                            />
                            {validationErrors.bookingDate && (
                                <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                            )}
                        </div>

                        <div className='input-container'>
                            <SimpleTimePickerSingle
                                selectedTime={selectedTime}
                                handleTimeSelect={handleTimeSelect}
                                date={bookingDate}
                            />
                            {validationErrors.selectedTime && (
                                <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                            )}
                        </div>

                    </div>
                    <div className='booking-submit-button-container'>
                        <button
                            type="submit"
                            onClick={() => handleBookRoom()}>
                            Book room
                        </button>
                    </div>
                </div>
            )}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>Thank you for booking the meeting room. Your selected time has been reserved on the calendar.</p>
                        <button onClick={closeModal}>
                            Close
                        </button>
                    </div>
                </div>
            )}
            {isErrorModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>
                            There was a problem with your booking.
                        </p>
                        <p className='error-code'>
                            {apiErrorMessage}
                        </p>
                        <button onClick={() => {
                            setIsErrorModalOpen(false);
                        }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Scheduler;